import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from '../utils/safeToast';
import { Key, Loader2, Eye, EyeOff, ShieldAlert } from 'lucide-react';

const API = "/api";

const ForcedPasswordChange = () => {
  const { clearForcePasswordChange, logout } = useAuth();
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password.length < 6) {
      toast.error('Das Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forced-password-change`, passwordData);
      toast.success('Passwort erfolgreich geändert');
      clearForcePasswordChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-amber-600" />
          </div>
          <CardTitle className="font-['Manrope'] text-xl">Passwort ändern erforderlich</CardTitle>
          <CardDescription>
            Ihr Passwort wurde zurückgesetzt. Bitte wählen Sie ein neues Passwort, um fortzufahren.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="bg-stone-50 pr-10"
                  placeholder="Mindestens 6 Zeichen"
                  data-testid="forced-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Passwort bestätigen</Label>
              <Input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                className="bg-stone-50"
                placeholder="Passwort wiederholen"
                data-testid="forced-confirm-password"
              />
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <Button
                type="submit"
                className="w-full bg-[#0E4C92] hover:bg-[#0A3A70] rounded-lg"
                disabled={submitting}
                data-testid="forced-password-submit"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Passwort speichern
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={logout}
                className="w-full text-stone-500"
              >
                Abmelden
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcedPasswordChange;
